import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { IntakeData } from "../../pages/Index";
import { patientIntakeApi } from "@/lib/patientIntakeApi";
import {
  FileText,
  Camera,
  User,
  Heart,
  Plane,
  Shield,
  CheckCircle,
  RotateCcw,
  Eye,
  X,
  Clock,
} from "lucide-react";
import { formatDateToMMDDYYYY } from "../../lib/dateUtils";

interface ReviewStepProps {
  data: IntakeData;
  onUpdate: (data: Partial<IntakeData>) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  onRetakePhoto?: (payload: { photoId: string; mediaId?: string }) => void;
}

const ORTHODONTIC_PHOTO_LABELS = {
  upper_occlusal: "Upper Occlusal",
  frontal_retracted_apart: "Frontal Retracted Apart",
  lower_occlusal: "Lower Occlusal",
  right_buccal: "Right Buccal",
  frontal_in_occlusion: "Frontal In Occlusion",
  left_buccal: "Left Buccal",
};

const PHOTO_LABELS = {
  front: "Front Smile",
  leftProfile: "Left Profile",
  rightProfile: "Right Profile",
  upperArch: "Upper Arch",
  lowerArch: "Lower Arch",
  biteFront: "Bite Closed",
};

const TRAVEL_PLAN_LABELS: Record<string, string> = {
  Yes: "Traveling alone",
  No: "Traveling with someone",
  "No, with partner": "Traveling with partner",
  "No, with family": "Traveling with family",
  "No, with friends": "Traveling with friends",
  yes: "Traveling alone",
  no: "Traveling with someone",
  considering: "Travel plans still being decided",
};

export default function ReviewStep({
  data,
  onUpdate,
  onSubmit,
  isSubmitting,
  onRetakePhoto,
}: ReviewStepProps) {
  const [consentGiven, setConsentGiven] = useState(data.consentGiven);
  const [selectedPhoto, setSelectedPhoto] = useState<{
    id: string;
    title: string;
    image: string;
    mediaId?: string;
  } | null>(null);
  const [isLoadingPhotos, setIsLoadingPhotos] = useState(false);
  const lastHydratedKeyRef = useRef<string | null>(null);
  const hasFetchedOnceRef = useRef(false);

  const getTravelPlanLabel = (value?: string) => {
    if (!value) return "Travel plans not specified";
    const trimmed = value.trim();
    return (
      TRAVEL_PLAN_LABELS[trimmed] ??
      TRAVEL_PLAN_LABELS[trimmed.toLowerCase()] ??
      "Travel plans not specified"
    );
  };

  const handleConsentChange = (checked: boolean) => {
    setConsentGiven(checked);
    onUpdate({ consentGiven: checked });
  };

  const handleSubmit = () => {
    if (!consentGiven) {
      // Scroll to consent checkbox if not checked
      setTimeout(() => {
        const consentElement = document.getElementById("consent");
        if (consentElement) {
          consentElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          setTimeout(() => {
            consentElement.focus();
          }, 500);
        }
      }, 100);
      return;
    }

    // Check if we have at least some valid photos
    if (validPhotos.length === 0 && Object.keys(data.photos || {}).length > 0) {
      alert(
        "Please retake your photos as they failed to upload properly. We need at least one valid photo to process your intake.",
      );
      return;
    }

    onSubmit();
  };

  const openPhotoModal = useCallback(
    (photoId: string, title: string, image: string, mediaId?: string) => {
      setSelectedPhoto({ id: photoId, title, image, mediaId });
    },
    [],
  );

  const closePhotoModal = useCallback(() => {
    setSelectedPhoto(null);
  }, []);

  const handleRetakePhoto = useCallback(
    (photoId: string) => {
      if (onRetakePhoto) {
        const mediaId =
          selectedPhoto?.mediaId ??
          (data.photoMediaMap ? data.photoMediaMap[photoId] : undefined);
        onRetakePhoto({ photoId, mediaId });
        closePhotoModal();
      }
    },
    [onRetakePhoto, closePhotoModal, selectedPhoto, data.photoMediaMap],
  );

  // Validate photo URLs - data URLs are normal for captured photos
  const validPhotos = Object.entries(data.photos || {}).filter(
    ([_, url]) => url && typeof url === "string" && url.length > 0,
  );
  // No invalid photos - data URLs are expected for captured photos

  const orthodonticPhotoCount = validPhotos.length;

  const localStateSnapshot = useMemo(() => {
    const photoValues = Object.values(data.photos || {});
    const additionalValues = data.additionalPhotos || [];
    const isLocalUrl = (url: unknown) =>
      typeof url === "string" &&
      (url.startsWith("data:") || url.startsWith("blob:"));

    const localPhotoCount = photoValues.filter(isLocalUrl).length;
    const localAdditionalCount = additionalValues.filter(isLocalUrl).length;

    return {
      localPhotoCount,
      localAdditionalCount,
    };
  }, [data.photos, data.additionalPhotos]);

  const hasLocalPrimaryPhotos = localStateSnapshot.localPhotoCount > 0;
  const hasLocalAdditionalPhotos = localStateSnapshot.localAdditionalCount > 0;

  const localHydrationKey = useMemo(
    () =>
      `${data.formUid ?? ""}:${localStateSnapshot.localPhotoCount}:${localStateSnapshot.localAdditionalCount}`,
    [
      data.formUid,
      localStateSnapshot.localPhotoCount,
      localStateSnapshot.localAdditionalCount,
    ],
  );

  const arraysShallowEqual = useCallback((a: string[], b: string[]) => {
    if (a.length !== b.length) {
      return false;
    }
    for (let i = 0; i < a.length; i += 1) {
      if (a[i] !== b[i]) {
        return false;
      }
    }
    return true;
  }, []);

  useEffect(() => {
    if (!data.formUid) {
      return;
    }

    const shouldFetch =
      !hasFetchedOnceRef.current ||
      hasLocalPrimaryPhotos ||
      hasLocalAdditionalPhotos ||
      lastHydratedKeyRef.current !== localHydrationKey;

    if (!shouldFetch) {
      return;
    }

    lastHydratedKeyRef.current = localHydrationKey;

    let isCancelled = false;

    const hydratePhotosFromBackend = async () => {
      try {
        setIsLoadingPhotos(true);
        const response = await patientIntakeApi.getIntakeStatus(data.formUid!);
        const mediaItems: Array<{ [key: string]: any }> =
          response.data?.media ?? [];

        if (
          isCancelled ||
          !Array.isArray(mediaItems) ||
          mediaItems.length === 0
        ) {
          return;
        }

        const sortedMedia = [...mediaItems].sort((a, b) => {
          const seqA =
            typeof a.photoSequence === "number"
              ? a.photoSequence
              : Number.MAX_SAFE_INTEGER;
          const seqB =
            typeof b.photoSequence === "number"
              ? b.photoSequence
              : Number.MAX_SAFE_INTEGER;
          if (seqA !== seqB) {
            return seqA - seqB;
          }
          const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return dateA - dateB;
        });

        const remotePhotos: Record<string, { url: string; mediaId: string }> =
          {};
        const remotePhotoMediaIds: Record<string, string> = {};
        const remoteAdditionalPhotos: { url: string; mediaId: string }[] = [];
        const seenAdditionalMedia = new Set<string>();

        sortedMedia.forEach((item) => {
          const url =
            typeof item.downloadUrl === "string" ? item.downloadUrl : undefined;
          if (!url) {
            return;
          }

          const position = item.photoPosition as string | undefined;
          if (
            position &&
            (position in ORTHODONTIC_PHOTO_LABELS || position in PHOTO_LABELS)
          ) {
            remotePhotos[position] = {
              url,
              mediaId: item.id,
            };
            remotePhotoMediaIds[position] = item.id;
            return;
          }

          if (item.id && !seenAdditionalMedia.has(item.id)) {
            remoteAdditionalPhotos.push({ url, mediaId: item.id });
            seenAdditionalMedia.add(item.id);
            return;
          }

          if (
            !item.id &&
            !remoteAdditionalPhotos.some((photo) => photo.url === url)
          ) {
            remoteAdditionalPhotos.push({ url, mediaId: url });
          }
        });

        const updates: Partial<IntakeData> = {};
        const existingPhotos = data.photos || {};

        const shouldUpdatePhotos = Object.entries(remotePhotos).some(
          ([key, value]) =>
            existingPhotos[key as keyof typeof existingPhotos] !== value.url,
        );

        if (shouldUpdatePhotos) {
          updates.photos = {
            ...existingPhotos,
            ...Object.fromEntries(
              Object.entries(remotePhotos).map(([key, value]) => [
                key,
                value.url,
              ]),
            ),
          };
        }

        if (Object.keys(remotePhotoMediaIds).length > 0) {
          updates.photoMediaMap = {
            ...(data.photoMediaMap ?? {}),
            ...remotePhotoMediaIds,
          };
        }

        if (remoteAdditionalPhotos.length > 0) {
          const newAdditional = remoteAdditionalPhotos.map(
            (photo) => photo.url,
          );
          const existingAdditional = data.additionalPhotos || [];

          if (!arraysShallowEqual(newAdditional, existingAdditional)) {
            updates.additionalPhotos = newAdditional;
            updates.additionalPhotoMediaIds = remoteAdditionalPhotos.map(
              (photo) => photo.mediaId,
            );
          } else if (
            !arraysShallowEqual(
              remoteAdditionalPhotos.map((photo) => photo.mediaId),
              data.additionalPhotoMediaIds ?? [],
            )
          ) {
            updates.additionalPhotoMediaIds = remoteAdditionalPhotos.map(
              (photo) => photo.mediaId,
            );
          }
        }

        if (!isCancelled && Object.keys(updates).length > 0) {
          onUpdate(updates);
        }
      } catch (error) {
        console.error("Failed to hydrate intake photos from backend:", error);
      } finally {
        if (!isCancelled) {
          setIsLoadingPhotos(false);
          hasFetchedOnceRef.current = true;
          lastHydratedKeyRef.current = localHydrationKey;
        }
      }
    };

    void hydratePhotosFromBackend();

    return () => {
      isCancelled = true;
    };
  }, [
    data.formUid,
    localHydrationKey,
    hasLocalPrimaryPhotos,
    hasLocalAdditionalPhotos,
    onUpdate,
  ]);

  return (
    <>
      {/* Photo Modal */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={closePhotoModal}
        >
          <div
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h3 className="heading-tertiary text-gray-900">
                {selectedPhoto.title}
              </h3>
              <div className="flex items-center gap-3">
                {onRetakePhoto &&
                  !selectedPhoto.id.startsWith("additional_") && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRetakePhoto(selectedPhoto.id)}
                      className="flex items-center gap-2 rounded-xl border-2 hover:border-[#166B93] hover:text-[#166B93]"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Retake Photo
                    </Button>
                  )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={closePhotoModal}
                  className="rounded-xl"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              <div className="aspect-[3/4] bg-gray-100 rounded-xl overflow-hidden shadow-inner">
                <img
                  src={selectedPhoto.image}
                  alt={selectedPhoto.title}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Photo Info */}
              <div className="mt-4 text-center">
                <p className="body-small text-gray-600">
                  Click "Retake Photo" to capture a new image for this position
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Submission Loading Overlay */}
      {isSubmitting && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-2xl">
            <div className="w-16 h-16 mx-auto mb-4 relative">
              <div className="w-16 h-16 border-4 border-[#166B93]/20 rounded-full"></div>
              <div className="w-16 h-16 border-4 border-[#166B93] border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
            </div>
            <h3 className="heading-tertiary text-gray-900 mb-2">
              Processing Your Intake
            </h3>
            <p className="body-base text-gray-600 mb-4">
              Please don't close this window or navigate away
            </p>
            <div className="bg-blue-50 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-left">
                  <p className="body-small text-blue-800 font-medium mb-1">
                    This usually takes 30-60 seconds
                  </p>
                  <p className="text-xs text-blue-600">
                    We're securely uploading your photos and creating your
                    profile
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Layout */}
      <div className="lg:hidden space-y-6">
        {/* Header */}
        <div className="text-center space-y-3">
          <div className="w-16 h-16 bg-[#166B93]/10 rounded-full flex items-center justify-center mx-auto">
            <FileText className="w-8 h-8 text-[#166B93]" />
          </div>
          <div>
            <h2 className="heading-secondary text-gray-900 mb-1">
              Review Your Information
            </h2>
            <p className="body-base text-gray-600">
              Please review your details before submitting
            </p>
          </div>
        </div>

        {/* Personal Information */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
          <div className="flex items-center gap-3 pb-3 border-b border-gray-100">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <User className="w-4 h-4 text-blue-600" />
            </div>
            <h3 className="font-heading font-semibold text-gray-900">
              Personal Information
            </h3>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4">
              <div>
                <span className="body-small text-gray-500 block mb-1">
                  Full Name
                </span>
                <p className="body-base font-medium text-gray-900">
                  {data.firstName} {data.lastName}
                </p>
              </div>
              <div>
                <span className="body-small text-gray-500 block mb-1">
                  Email Address
                </span>
                <p className="body-base font-medium text-gray-900 break-all">
                  {data.email}
                </p>
              </div>
              <div>
                <span className="body-small text-gray-500 block mb-1">
                  Phone Number
                </span>
                <p className="body-base font-medium text-gray-900">
                  {data.phone}
                </p>
              </div>
              {data.dateOfBirth && (
                <div>
                  <span className="body-small text-gray-500 block mb-1">
                    Date of Birth
                  </span>
                  <p className="body-base font-medium text-gray-900">
                    {formatDateToMMDDYYYY(data.dateOfBirth)}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Medical Information */}
        {(data.dentalProcedureType ||
          data.preExistingConditions ||
          data.currentMedications ||
          data.allergies ||
          data.dentalDiscomfort ||
          data.smokingHabits) && (
          <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
            <div className="flex items-center gap-3 pb-3 border-b border-gray-100">
              <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                <Heart className="w-4 h-4 text-red-600" />
              </div>
              <h3 className="font-heading font-semibold text-gray-900">
                Medical Information
              </h3>
            </div>

            <div className="space-y-4">
              {data.dentalProcedureType && (
                <div>
                  <span className="body-small text-gray-500 block mb-1">
                    Dental Procedure
                  </span>
                  <p className="body-base font-medium text-gray-900">
                    {data.dentalProcedureType}
                  </p>
                </div>
              )}

              {data.preExistingConditions && (
                <div>
                  <span className="body-small text-gray-500 block mb-1">
                    Medical Conditions
                  </span>
                  <p className="body-base font-medium text-gray-900">
                    {data.preExistingConditions}
                  </p>
                </div>
              )}

              {data.currentMedications && (
                <div>
                  <span className="body-small text-gray-500 block mb-1">
                    Current Medications
                  </span>
                  <p className="body-base font-medium text-gray-900">
                    {data.currentMedications}
                  </p>
                </div>
              )}

              {data.allergies && (
                <div>
                  <span className="body-small text-gray-500 block mb-1">
                    Allergies
                  </span>
                  <p className="body-base font-medium text-gray-900">
                    {data.allergies}
                  </p>
                </div>
              )}

              {data.dentalDiscomfort && (
                <div>
                  <span className="body-small text-gray-500 block mb-1">
                    Dental Discomfort
                  </span>
                  <p className="body-base font-medium text-gray-900">
                    {data.dentalDiscomfort}
                  </p>
                </div>
              )}

              {data.smokingHabits && (
                <div>
                  <span className="body-small text-gray-500 block mb-1">
                    Smoking Habits
                  </span>
                  <p className="body-base font-medium text-gray-900">
                    {data.smokingHabits}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Travel Information */}
        {(data.budgetExpectations ||
          data.travelAlone ||
          data.travelConcerns ||
          data.personalInterest) && (
          <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
            <div className="flex items-center gap-3 pb-3 border-b border-gray-100">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <Plane className="w-4 h-4 text-green-600" />
              </div>
              <h3 className="font-heading font-semibold text-gray-900">
                Travel & Budget
              </h3>
            </div>

            <div className="space-y-4">
              {data.budgetExpectations && (
                <div>
                  <span className="body-small text-gray-500 block mb-1">
                    Budget Expectations
                  </span>
                  <p className="body-base font-medium text-gray-900">
                    {data.budgetExpectations}
                  </p>
                </div>
              )}

              {data.travelAlone && (
                <div>
                  <span className="body-small text-gray-500 block mb-1">
                    Travel Plans
                  </span>
                  <p className="body-base font-medium text-gray-900">
                    {getTravelPlanLabel(data.travelAlone)}
                  </p>
                </div>
              )}

              {data.travelConcerns && (
                <div>
                  <span className="body-small text-gray-500 block mb-1">
                    Travel Concerns
                  </span>
                  <p className="body-base font-medium text-gray-900">
                    {data.travelConcerns}
                  </p>
                </div>
              )}

              {data.personalInterest && (
                <div>
                  <span className="body-small text-gray-500 block mb-1">
                    Interest in Colombia
                  </span>
                  <p className="body-base font-medium text-gray-900">
                    {data.personalInterest}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Photos Section */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                <Camera className="w-4 h-4 text-purple-600" />
              </div>
              <h3 className="font-heading font-semibold text-gray-900">
                Dental Photos
              </h3>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="body-small font-medium text-green-800">
                {orthodonticPhotoCount} photos
              </span>
            </div>
          </div>

          {isLoadingPhotos && (
            <div className="flex items-center gap-2 text-xs text-blue-600">
              <Clock className="w-4 h-4" />
              <span>Refreshing your photos…</span>
            </div>
          )}

          {/* Photo Grid */}
          {orthodonticPhotoCount > 0 ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {validPhotos.map(([key, url]) => {
                  const title =
                    ORTHODONTIC_PHOTO_LABELS[
                      key as keyof typeof ORTHODONTIC_PHOTO_LABELS
                    ] ||
                    PHOTO_LABELS[key as keyof typeof PHOTO_LABELS] ||
                    key
                      .replace(/([A-Z])/g, " $1")
                      .replace(/^./, (str) => str.toUpperCase());

                  return (
                    <Card
                      key={key}
                      className="group cursor-pointer hover:shadow-lg transition-all duration-200 border-2 hover:border-[#166B93]/30 rounded-xl"
                    >
                      <CardContent className="p-3">
                        <div className="aspect-[3/4] bg-gray-100 rounded-lg overflow-hidden mb-2 relative">
                          <img
                            src={url as string}
                            alt={title}
                            className="w-full h-full object-cover"
                          />

                          {/* Hover Actions */}
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                            <Button
                              size="sm"
                              variant="secondary"
                              className="bg-white/90 hover:bg-white rounded-lg"
                              onClick={() =>
                                openPhotoModal(
                                  key,
                                  title,
                                  url as string,
                                  data.photoMediaMap?.[key],
                                )
                              }
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {onRetakePhoto && (
                              <Button
                                size="sm"
                                variant="secondary"
                                className="bg-white/90 hover:bg-white rounded-lg"
                                onClick={() => handleRetakePhoto(key)}
                              >
                                <RotateCcw className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-center font-medium text-gray-700 leading-tight">
                          {title}
                        </p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Additional Photos */}
              {data.additionalPhotos && data.additionalPhotos.length > 0 && (
                <div className="bg-blue-50 rounded-xl p-4">
                  <h4 className="body-base font-medium text-blue-800 mb-3">
                    Additional Photos ({data.additionalPhotos.length})
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    {data.additionalPhotos.map((photo, index) => (
                      <Card
                        key={index}
                        className="group cursor-pointer hover:shadow-md transition-shadow rounded-xl"
                      >
                        <CardContent className="p-3">
                          <div className="aspect-[3/4] bg-gray-100 rounded-lg overflow-hidden mb-2 relative">
                            <img
                              src={photo}
                              alt={`Additional photo ${index + 1}`}
                              className="w-full h-full object-cover"
                            />

                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <Button
                                size="sm"
                                variant="secondary"
                                className="bg-white/90 hover:bg-white rounded-lg"
                                onClick={() =>
                                  openPhotoModal(
                                    `additional_${index}`,
                                    `Additional Photo ${index + 1}`,
                                    photo,
                                    data.additionalPhotoMediaIds?.[index],
                                  )
                                }
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          <p className="text-xs text-center font-medium text-blue-700">
                            Problem Area {index + 1}
                          </p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-gray-50 rounded-xl p-6 text-center">
              <Camera className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p className="body-base text-gray-600 mb-2">
                No photos captured yet
              </p>
              <p className="body-small text-gray-500">
                Photos are required for your dental evaluation
              </p>
            </div>
          )}
        </div>

        {/* Privacy and Consent */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
          <div className="flex items-center gap-3 pb-3 border-b border-gray-100">
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
              <Shield className="w-4 h-4 text-green-600" />
            </div>
            <h3 className="font-heading font-semibold text-gray-900">
              Privacy & Consent
            </h3>
          </div>

          <div className="bg-blue-50 rounded-xl p-4 space-y-3">
            <div className="flex items-start space-x-3">
              <Checkbox
                id="consent"
                checked={consentGiven}
                onCheckedChange={handleConsentChange}
                className="mt-1"
              />
              <div className="flex-1">
                <label
                  htmlFor="consent"
                  className="body-base font-medium text-blue-900 cursor-pointer block mb-2"
                >
                  I consent to securely sharing my photos and health information
                  for evaluation
                </label>
                <p className="body-small text-blue-700">
                  Your information will be encrypted, stored securely, and only
                  used for your dental consultation. You can request deletion at
                  any time.
                </p>
              </div>
            </div>

            <div className="text-xs text-blue-600 space-y-1 pt-2 border-t border-blue-200">
              <p>
                <a
                  href="https://jemaservices.com.co/privacy-policy/"
                  className="underline hover:text-blue-800"
                >
                  Privacy Policy
                </a>{" "}
                |
                <a
                  href="https://jemaservices.com.co/privacy-policy/"
                  className="underline hover:text-blue-800 ml-1"
                >
                  Security Information
                </a>
              </p>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="space-y-3">
          <Button
            onClick={handleSubmit}
            disabled={!consentGiven || isSubmitting}
            className="w-full shadow-lg hover:shadow-xl transform hover:scale-105 disabled:transform-none disabled:shadow-none"
            size="xl"
            variant="jema"
          >
            {isSubmitting ? (
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Processing...
              </div>
            ) : (
              "Submit My Dental Intake"
            )}
          </Button>

          {!consentGiven && (
            <p className="text-xs text-gray-500 text-center">
              Please provide consent to continue
            </p>
          )}
        </div>

        {/* What Happens Next */}
        <div className="bg-gradient-to-r from-[#166B93]/5 to-[#FF9142]/5 rounded-2xl p-6">
          <h4 className="font-heading font-semibold text-gray-900 mb-3">
            What happens next?
          </h4>
          <ul className="space-y-2">
            <li className="flex items-start gap-3">
              <div className="w-2 h-2 bg-[#166B93] rounded-full mt-2 flex-shrink-0"></div>
              <span className="body-small text-gray-700">
                Our dental team will review your photos and information
              </span>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-2 h-2 bg-[#CEF12F] rounded-full mt-2 flex-shrink-0"></div>
              <span className="body-small text-gray-700">
                You'll receive a personalized treatment plan within 24-48
                business hours
              </span>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-2 h-2 bg-[#FF9142] rounded-full mt-2 flex-shrink-0"></div>
              <span className="body-small text-gray-700">
                Schedule a free virtual consultation to discuss your options
              </span>
            </li>
          </ul>
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden lg:block max-w-4xl mx-auto">
        <div className="space-y-8">
          {/* Header */}
          <div className="text-center space-y-4">
            <h2 className="heading-primary text-gray-900">
              Review Your Information
            </h2>
            <p className="heading-tertiary text-gray-600">
              Please review your details before submitting
            </p>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
            {/* Left Column - Personal & Medical Info */}
            <div className="space-y-6">
              {/* Personal Information */}
              <div className="bg-white rounded-2xl border border-gray-100 p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="heading-tertiary text-gray-900">
                    Personal Information
                  </h3>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="body-base text-gray-500">Full Name</span>
                    <p className="body-base font-medium text-gray-900">
                      {data.firstName} {data.lastName}
                    </p>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="body-base text-gray-500">Email</span>
                    <p className="body-base font-medium text-gray-900">
                      {data.email}
                    </p>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="body-base text-gray-500">Phone</span>
                    <p className="body-base font-medium text-gray-900">
                      {data.phone}
                    </p>
                  </div>
                  {data.dateOfBirth && (
                    <div className="flex justify-between items-center py-3">
                      <span className="body-base text-gray-500">
                        Date of Birth
                      </span>
                      <p className="body-base font-medium text-gray-900">
                        {formatDateToMMDDYYYY(data.dateOfBirth)}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Medical Information */}
              {(data.dentalProcedureType ||
                data.preExistingConditions ||
                data.currentMedications ||
                data.allergies ||
                data.dentalDiscomfort ||
                data.smokingHabits) && (
                <div className="bg-white rounded-2xl border border-gray-100 p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                      <Heart className="w-6 h-6 text-red-600" />
                    </div>
                    <h3 className="heading-tertiary text-gray-900">
                      Medical Information
                    </h3>
                  </div>

                  <div className="space-y-4">
                    {data.dentalProcedureType && (
                      <div className="flex justify-between items-start py-3 border-b border-gray-100">
                        <span className="body-base text-gray-500">
                          Dental Procedure
                        </span>
                        <p className="body-base font-medium text-gray-900 text-right max-w-xs">
                          {data.dentalProcedureType}
                        </p>
                      </div>
                    )}

                    {data.preExistingConditions && (
                      <div className="flex justify-between items-start py-3 border-b border-gray-100">
                        <span className="body-base text-gray-500">
                          Medical Conditions
                        </span>
                        <p className="body-base font-medium text-gray-900 text-right max-w-xs">
                          {data.preExistingConditions}
                        </p>
                      </div>
                    )}

                    {data.currentMedications && (
                      <div className="flex justify-between items-start py-3 border-b border-gray-100">
                        <span className="body-base text-gray-500">
                          Current Medications
                        </span>
                        <p className="body-base font-medium text-gray-900 text-right max-w-xs">
                          {data.currentMedications}
                        </p>
                      </div>
                    )}

                    {data.allergies && (
                      <div className="flex justify-between items-start py-3 border-b border-gray-100">
                        <span className="body-base text-gray-500">
                          Allergies
                        </span>
                        <p className="body-base font-medium text-gray-900 text-right max-w-xs">
                          {data.allergies}
                        </p>
                      </div>
                    )}

                    {data.dentalDiscomfort && (
                      <div className="flex justify-between items-start py-3 border-b border-gray-100">
                        <span className="body-base text-gray-500">
                          Dental Discomfort
                        </span>
                        <p className="body-base font-medium text-gray-900 text-right max-w-xs">
                          {data.dentalDiscomfort}
                        </p>
                      </div>
                    )}

                    {data.smokingHabits && (
                      <div className="flex justify-between items-start py-3">
                        <span className="body-base text-gray-500">
                          Smoking Habits
                        </span>
                        <p className="body-base font-medium text-gray-900 text-right max-w-xs">
                          {data.smokingHabits}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Travel Information */}
              {(data.budgetExpectations ||
                data.travelAlone ||
                data.travelConcerns ||
                data.personalInterest) && (
                <div className="bg-white rounded-2xl border border-gray-100 p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <Plane className="w-6 h-6 text-green-600" />
                    </div>
                    <h3 className="heading-tertiary text-gray-900">
                      Travel & Budget
                    </h3>
                  </div>

                  <div className="space-y-4">
                    {data.budgetExpectations && (
                      <div className="flex justify-between items-start py-3 border-b border-gray-100">
                        <span className="body-base text-gray-500">
                          Budget Expectations
                        </span>
                        <p className="body-base font-medium text-gray-900 text-right max-w-xs">
                          {data.budgetExpectations}
                        </p>
                      </div>
                    )}

                    {data.travelAlone && (
                      <div className="flex justify-between items-start py-3 border-b border-gray-100">
                        <span className="body-base text-gray-500">
                          Travel Plans
                        </span>
                        <p className="body-base font-medium text-gray-900 text-right max-w-xs">
                          {getTravelPlanLabel(data.travelAlone)}
                        </p>
                      </div>
                    )}

                    {data.travelConcerns && (
                      <div className="flex justify-between items-start py-3 border-b border-gray-100">
                        <span className="body-base text-gray-500">
                          Travel Concerns
                        </span>
                        <p className="body-base font-medium text-gray-900 text-right max-w-xs">
                          {data.travelConcerns}
                        </p>
                      </div>
                    )}

                    {data.personalInterest && (
                      <div className="flex justify-between items-start py-3">
                        <span className="body-base text-gray-500">
                          Interest in Colombia
                        </span>
                        <p className="body-base font-medium text-gray-900 text-right max-w-xs">
                          {data.personalInterest}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Photos */}
            <div className="space-y-6">
              {/* Photos Section */}
              <div className="bg-white rounded-2xl border border-gray-100 p-8">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                      <Camera className="w-6 h-6 text-purple-600" />
                    </div>
                    <h3 className="heading-tertiary text-gray-900">
                      Dental Photos
                    </h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="body-base font-medium text-green-800">
                      {orthodonticPhotoCount} photos captured
                    </span>
                  </div>
                </div>

                {isLoadingPhotos && (
                  <div className="flex items-center gap-2 text-sm text-blue-600 mb-4">
                    <Clock className="w-4 h-4" />
                    <span>Refreshing your photos…</span>
                  </div>
                )}

                {/* Photo Grid */}
                {orthodonticPhotoCount > 0 ? (
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-6">
                      {validPhotos.map(([key, url]) => {
                        const title =
                          ORTHODONTIC_PHOTO_LABELS[
                            key as keyof typeof ORTHODONTIC_PHOTO_LABELS
                          ] ||
                          PHOTO_LABELS[key as keyof typeof PHOTO_LABELS] ||
                          key
                            .replace(/([A-Z])/g, " $1")
                            .replace(/^./, (str) => str.toUpperCase());

                        return (
                          <Card
                            key={key}
                            className="group cursor-pointer hover:shadow-xl transition-all duration-300 border-2 hover:border-[#166B93]/40 rounded-xl"
                          >
                            <CardContent className="p-4">
                              <div className="aspect-[3/4] bg-gray-100 rounded-xl overflow-hidden mb-3 relative shadow-inner">
                                <img
                                  src={url as string}
                                  alt={title}
                                  className="w-full h-full object-cover"
                                />

                                {/* Hover Actions */}
                                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                                  <Button
                                    size="sm"
                                    variant="secondary"
                                    className="bg-white/95 hover:bg-white rounded-xl shadow-lg"
                                    onClick={() =>
                                      openPhotoModal(
                                        key,
                                        title,
                                        url as string,
                                        data.photoMediaMap?.[key],
                                      )
                                    }
                                  >
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                  {onRetakePhoto && (
                                    <Button
                                      size="sm"
                                      variant="secondary"
                                      className="bg-white/95 hover:bg-white rounded-xl shadow-lg"
                                      onClick={() => handleRetakePhoto(key)}
                                    >
                                      <RotateCcw className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                              <p className="text-sm text-center font-medium text-gray-700 leading-tight">
                                {title}
                              </p>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>

                    {/* Additional Photos */}
                    {data.additionalPhotos &&
                      data.additionalPhotos.length > 0 && (
                        <div className="bg-blue-50 rounded-xl p-6">
                          <h4 className="body-large font-medium text-blue-800 mb-4">
                            Additional Photos ({data.additionalPhotos.length})
                          </h4>
                          <div className="grid grid-cols-2 gap-6">
                            {data.additionalPhotos.map((photo, index) => (
                              <Card
                                key={index}
                                className="group cursor-pointer hover:shadow-md transition-shadow rounded-xl"
                              >
                                <CardContent className="p-4">
                                  <div className="aspect-[3/4] bg-gray-100 rounded-xl overflow-hidden mb-3 relative">
                                    <img
                                      src={photo}
                                      alt={`Additional photo ${index + 1}`}
                                      className="w-full h-full object-cover"
                                    />

                                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                      <Button
                                        size="sm"
                                        variant="secondary"
                                        className="bg-white/90 hover:bg-white rounded-xl"
                                        onClick={() =>
                                          openPhotoModal(
                                            `additional_${index}`,
                                            `Additional Photo ${index + 1}`,
                                            photo,
                                            data.additionalPhotoMediaIds?.[
                                              index
                                            ],
                                          )
                                        }
                                      >
                                        <Eye className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                  <p className="text-sm text-center font-medium text-blue-700">
                                    Problem Area {index + 1}
                                  </p>
                                </CardContent>
                              </Card>
                            ))}
                          </div>
                        </div>
                      )}
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-xl p-8 text-center">
                    <Camera className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                    <p className="body-large text-gray-600 mb-2">
                      No photos captured yet
                    </p>
                    <p className="body-base text-gray-500">
                      Photos are required for your dental evaluation
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Bottom Section - Consent & Submit */}
          <div className="space-y-6">
            {/* Privacy and Consent */}
            <div className="bg-white rounded-2xl border border-gray-100 p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Shield className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="heading-tertiary text-gray-900">
                  Privacy & Consent
                </h3>
              </div>

              <div className="bg-blue-50 rounded-xl p-6">
                <div className="flex items-start space-x-4">
                  <Checkbox
                    id="consent"
                    checked={consentGiven}
                    onCheckedChange={handleConsentChange}
                    className="mt-1.5 scale-125"
                  />
                  <div className="flex-1">
                    <label
                      htmlFor="consent"
                      className="body-large font-medium text-blue-900 cursor-pointer block mb-3"
                    >
                      I consent to securely sharing my photos and health
                      information for evaluation
                    </label>
                    <p className="body-base text-blue-700 mb-4">
                      Your information will be encrypted, stored securely, and
                      only used for your dental consultation. You can request
                      deletion at any time.
                    </p>
                    <div className="body-small text-blue-600">
                      <a
                        href="https://jemaservices.com.co/privacy-policy/"
                        className="underline hover:text-blue-800 mr-4"
                      >
                        Privacy Policy
                      </a>
                      <a
                        href="https://jemaservices.com.co/privacy-policy/"
                        className="underline hover:text-blue-800"
                      >
                        Security Information
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Section */}
            <div className="text-center space-y-6">
              <Button
                onClick={handleSubmit}
                disabled={!consentGiven || isSubmitting}
                className="px-12 py-6 text-lg font-semibold shadow-xl hover:shadow-2xl transform hover:scale-105 disabled:transform-none disabled:shadow-lg"
                variant="jema"
              >
                {isSubmitting ? (
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                    Processing Your Intake...
                  </div>
                ) : (
                  "Submit My Dental Intake"
                )}
              </Button>

              {!consentGiven && (
                <p className="body-base text-gray-500">
                  Please provide consent to continue
                </p>
              )}

              {/* What Happens Next */}
              <div className="bg-gradient-to-r from-[#166B93]/5 via-[#CEF12F]/5 to-[#FF9142]/5 rounded-2xl p-8 max-w-3xl mx-auto">
                <h4 className="heading-tertiary text-gray-900 mb-4">
                  What happens next?
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-[#166B93] rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-white font-bold">1</span>
                    </div>
                    <p className="body-base text-gray-700">
                      Our dental team will review your photos and information
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-[#CEF12F] rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-gray-900 font-bold">2</span>
                    </div>
                    <p className="body-base text-gray-700">
                      You'll receive a personalized treatment plan within 24-48
                      business hours
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-[#FF9142] rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-white font-bold">3</span>
                    </div>
                    <p className="body-base text-gray-700">
                      Schedule a free virtual consultation to discuss your
                      options
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
